import { create } from "zustand";
import { VerticalBrowseItem } from "../services/verticalBrowseService";

type BrowseMode = "folders" | "verticals";
type TaxonomyStatus = "idle" | "loading" | "loaded" | "error";
type ContentStatus = "idle" | "loading" | "loaded" | "error";
type BrowseAvailability = "pending" | "allowed" | "denied" | "error";

interface VerticalBrowseState {
  mode: BrowseMode;
  selectedVerticalId: string | null;
  selectedVerticalName: string | null;
  verticals: VerticalBrowseItem[];
  taxonomyStatus: TaxonomyStatus;
  taxonomyError: string | null;
  contentStatus: ContentStatus;
  contentError: string | null;
  browseAvailability: BrowseAvailability;
}

interface VerticalBrowseActions {
  setMode: (mode: BrowseMode) => void;
  setSelectedVertical: (id: string, name: string) => void;
  clearSelectedVertical: () => void;
  setVerticals: (verticals: VerticalBrowseItem[]) => void;
  setTaxonomyStatus: (status: TaxonomyStatus) => void;
  setTaxonomyError: (error: string | null) => void;
  setContentStatus: (status: ContentStatus) => void;
  setContentError: (error: string | null) => void;
  //  boolean→tri-state compatibility seam; true→allowed, false→denied
  setBrowseAvailable: (available: boolean) => void;
  setBrowseAvailabilityError: () => void;
  retryBrowseAvailability: () => void;
}

export const useVerticalBrowseStore = create<VerticalBrowseState & VerticalBrowseActions>((set) => ({
  // State
  mode: "folders",
  selectedVerticalId: null,
  selectedVerticalName: null,
  verticals: [],
  taxonomyStatus: "idle",
  taxonomyError: null,
  contentStatus: "idle",
  contentError: null,
  browseAvailability: "pending",

  // Actions
  setMode: (mode) =>
    set((state) => ({
      mode,
      selectedVerticalId: mode === "folders" ? null : state.selectedVerticalId,
      selectedVerticalName: mode === "folders" ? null : state.selectedVerticalName,
    })),

  setSelectedVertical: (id, name) => set({ selectedVerticalId: id, selectedVerticalName: name }),

  clearSelectedVertical: () => set({ selectedVerticalId: null, selectedVerticalName: null }),

  setVerticals: (verticals) => set((state) => {
    const selectedId = state.selectedVerticalId;
    if (!selectedId) return { verticals };
    const found = verticals.find((v) => v.id === selectedId);
    if (found) return { verticals, selectedVerticalName: found.name };
    return { verticals, selectedVerticalId: null, selectedVerticalName: null };
  }),

  setTaxonomyStatus: (status) => set({ taxonomyStatus: status }),

  setTaxonomyError: (error) => set({ taxonomyError: error }),

  setContentStatus: (status) => set({ contentStatus: status }),

  setContentError: (error) => set({ contentError: error }),

  setBrowseAvailable: (available) =>
    set({
      browseAvailability: available ? "allowed" : "denied",
      ...(available
        ? {}
        : {
            mode: "folders",
            selectedVerticalId: null,
            selectedVerticalName: null,
            verticals: [],
            taxonomyStatus: "idle",
            taxonomyError: null,
            contentStatus: "idle",
            contentError: null,
          }),
    }),

  setBrowseAvailabilityError: () =>
    set({
      browseAvailability: "error",
      mode: "folders",
      selectedVerticalId: null,
      selectedVerticalName: null,
      verticals: [],
      taxonomyStatus: "idle",
      taxonomyError: null,
      contentStatus: "idle",
      contentError: null,
    }),

  retryBrowseAvailability: () => set({ browseAvailability: "pending" }),
}));
